import apiClient from '../api/client';
import { ECPT_ENDPOINTS } from '../api/endpoints';
import type {
  GetOperationOverviewViewsParams,
  GetOperationOverviewViewsResponse,
  GetOperationOverviewDataParams,
  GetOperationOverviewDataResponse,
  GetOperationOverviewDataV2Params,
  GetOperationOverviewDataV2Response,
  PatchOperationOverviewGetCaseBody,
  PatchOperationOverviewGetCaseResponse,
  PatchPemStatusBody,
  PatchPemStatusResponse,
  PatchPemCompletionStatusBody,
  PatchPemCompletionStatusResponse,
  PatchPinBody,
  PatchPinResponse,
  SearchPemParams,
  SearchPemResponse,
  GetExportDataParams,
} from '../../types/api';

export function buildCombinedTableQueryParams<T extends {
  view_id: string;
  user_id: string;
  page?: number;
  page_size?: number;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  search_text?: string;
  filters?: Record<string, string>;
}>(params: T): URLSearchParams {
  const searchParams = new URLSearchParams();

  const entries: Array<[string, string]> = [
    ['view_id', String(params.view_id)],
    ['user_id', String(params.user_id)],
  ];

  if (params.page != null) entries.push(['page', String(params.page)]);
  if (params.page_size != null) entries.push(['page_size', String(params.page_size)]);
  if (params.sort_by) entries.push(['sort_by', String(params.sort_by)]);
  if (params.sort_order) entries.push(['sort_order', String(params.sort_order)]);

  const searchText = params.search_text?.trim();
  if (searchText) entries.push(['search_text', searchText]);

  entries.forEach(([key, value]) => searchParams.set(key, value));

  Object.entries(params.filters ?? {})
    .forEach(([key, value]) => {
      const normalizedKey = key.trim();
      const normalizedValue = value?.trim();
      if (!normalizedKey || !normalizedValue) return;
      searchParams.append('filters', `${normalizedKey}:${normalizedValue}`);
    });

  return searchParams;
}

const ecptOperationOverviewService = {
  /**
   * GET /operation-overview/views
   * Lightweight view list for the dropdown.
   */
  getViews: async (
    params: GetOperationOverviewViewsParams,
  ): Promise<GetOperationOverviewViewsResponse> => {
    const { data } = await apiClient.get<GetOperationOverviewViewsResponse>(
      ECPT_ENDPOINTS.OPERATION_OVERVIEW_VIEWS,
      { params },
    );
    return data;
  },

  /**
   * GET /operation-overview/data
   * Dynamic pem_info data for the selected view, with pagination.
   */
  getData: async (
    params: GetOperationOverviewDataParams,
  ): Promise<GetOperationOverviewDataResponse> => {
    const searchParams = buildCombinedTableQueryParams(params);
    const { data } = await apiClient.get<GetOperationOverviewDataResponse>(
      `${ECPT_ENDPOINTS.OPERATION_OVERVIEW_DATA}?${searchParams.toString()}`,
    );
    return data;
  },

  /**
   * GET /operation-overview/data/v2
   * Server-side grouped data with PEM-based pagination.
   */
  getDataV2: async (
    params: GetOperationOverviewDataV2Params,
  ): Promise<GetOperationOverviewDataV2Response> => {
    const searchParams = buildCombinedTableQueryParams(params);
    const { data } = await apiClient.get<GetOperationOverviewDataV2Response>(
      `${ECPT_ENDPOINTS.OPERATION_OVERVIEW_DATA_V2}?${searchParams.toString()}`,
    );
    return data;
  },

  /**
   * PATCH /operation-overview/get-case
   * Updates Get Case flag for the given PEM ID.
   */
  updateGetCase: async (
    body: PatchOperationOverviewGetCaseBody,
  ): Promise<PatchOperationOverviewGetCaseResponse> => {
    const { data } = await apiClient.patch<PatchOperationOverviewGetCaseResponse>(
      ECPT_ENDPOINTS.OPERATION_OVERVIEW_GET_CASE,
      body,
    );
    return data;
  },

  /**
   * PATCH /operation-overview/pem-status
   * Updates PEM status for the given PEM ID. Pass pem_status: null to clear.
   */
  updatePemStatus: async (
    body: PatchPemStatusBody,
  ): Promise<PatchPemStatusResponse> => {
    const { data } = await apiClient.patch<PatchPemStatusResponse>(
      ECPT_ENDPOINTS.OPERATION_OVERVIEW_PEM_STATUS,
      body,
    );
    return data;
  },

  /** Updates the independent PEM completion status for the selected module. */
  updatePemCompletionStatus: async (
    module: 'operation-overview' | 'get-case',
    body: PatchPemCompletionStatusBody,
  ): Promise<PatchPemCompletionStatusResponse> => {
    const endpoint = module === 'get-case'
      ? ECPT_ENDPOINTS.GET_CASE_PEM_COMPLETION_STATUS
      : ECPT_ENDPOINTS.OPERATION_OVERVIEW_PEM_COMPLETION_STATUS;
    const { data } = await apiClient.patch<PatchPemCompletionStatusResponse>(endpoint, body);
    return data;
  },

  /**
   * GET /operation-overview/search-pem
   * Autocomplete PEM ID search within a view's date range. Min 3 chars, max 20 results.
   */
  searchPem: async (params: SearchPemParams): Promise<SearchPemResponse> => {
    const { data } = await apiClient.get<SearchPemResponse>(
      ECPT_ENDPOINTS.OPERATION_OVERVIEW_SEARCH_PEM,
      { params },
    );
    return data;
  },

  /**
   * PATCH /operation-overview/pin
   * Pins or unpins a PEM. Shared between Operation Overview and GET Cases.
   */
  updatePin: async (body: PatchPinBody): Promise<PatchPinResponse> => {
    const { data } = await apiClient.patch<PatchPinResponse>(
      ECPT_ENDPOINTS.OPERATION_OVERVIEW_PIN,
      body,
    );
    return data;
  },

  /**
   * PATCH /operation-overview/custom-field-value  (or /get-case/custom-field-value)
   * UPSERT a custom field cell value for a pem_info row.
   * Pass value: null or "" to clear the cell.
   */
  upsertCustomFieldValue: async (
    module: 'operation-overview' | 'get-case',
    body: ({ pem_info_id: string; pem_id?: never } | { pem_id: string; pem_info_id?: never }) & { column_id: number; value: string | null },
  ): Promise<{ success: boolean; message: string; column_id: number; value: string | null }> => {
    const endpoint =
      module === 'get-case'
        ? ECPT_ENDPOINTS.GET_CASE_CUSTOM_FIELD_VALUE
        : ECPT_ENDPOINTS.OPERATION_OVERVIEW_CUSTOM_FIELD_VALUE;
    const { data } = await apiClient.patch(endpoint, body);
    return data;
  },

  /**
   * GET /export/data
   * Downloads the view data as a file. Triggers browser download via a
   * temporary <a> element — returns void (the side-effect is the download).
   */
  exportData: async (params: GetExportDataParams): Promise<void> => {
    const query = new URLSearchParams();
    query.set('view_id', params.view_id);
    query.set('user_id', params.user_id);
    query.set('module', params.module);
    query.set('format', params.format);
    params.columns.forEach(column => query.append('columns', column));
    const response = await apiClient.get<Blob>(ECPT_ENDPOINTS.EXPORT_DATA, {
      params: query,
      responseType: 'blob',
    });

    const disposition: string = (response.headers['content-disposition'] as string) ?? '';
    let filename = `ECPT_Export.${params.format}`;
    const match = disposition.match(/filename="?([^";\n]+)"?/);
    if (match?.[1]) filename = match[1];

    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

export default ecptOperationOverviewService;
